/*
 * Replaces the gift picker's variant dropdown with the storefront's own
 * swatches, so choosing a free gift looks like choosing any other product.
 *
 * The picker is rendered by Goalify and offers one <select> per gift whose
 * options are whole variants ("Black / S"), not separate colour and size
 * axes. This splits those labels back into axes, draws a swatch row per axis,
 * and writes the shopper's combination back to the original <select>, which
 * stays in the DOM as the source of truth.
 *
 * Two constraints shape the implementation:
 *
 * - The picker is a React component that remounts, so enhancement is driven
 *   by a MutationObserver and each select is marked once it is handled.
 * - Assigning to select.value does not reach React's internal state. The
 *   value has to go through the prototype's native setter before a bubbling
 *   change event, which is what React listens for.
 *
 * Selects with disabled options retain the native control and vendor stock
 * labels. For enhanced selects, a missing colour/size combination renders
 * as unavailable.
 */

(() => {
  const SELECT = ".n99gb-giftSelect";
  const ENHANCED = "data-umi-swatches";
  const SEPARATOR = " / ";

  // Option values double as CSS colour keywords for the shades UMI sells.
  // Anything unrecognised falls back to a neutral chip rather than vanishing.
  const SWATCH_COLORS = {
    black: "#121212",
    beige: "#d9c7ad",
    ivory: "rgb(241 232 218)",
    brown: "#6b4f3a",
  };

  const setSelectValue = (select, value) => {
    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLSelectElement.prototype,
      "value",
    ).set;
    nativeSetter.call(select, value);
    select.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const readOptions = (select) =>
    Array.from(select.options).map((option) => ({
      value: option.value,
      parts: option.text.split(SEPARATOR).map((part) => part.trim()),
    }));

  /* Distinct values for one axis, in the order the options present them. */
  const axisValues = (options, index) => [
    ...new Set(options.map((option) => option.parts[index]).filter(Boolean)),
  ];

  const findVariant = (options, chosen) =>
    options.find((option) =>
      chosen.every((value, index) => option.parts[index] === value),
    );

  const buildSwatch = (value) => {
    const swatch = document.createElement("span");
    swatch.className = "swatch";
    const color = SWATCH_COLORS[value.toLowerCase()] || value.toLowerCase();
    swatch.style.setProperty("--swatch--background", color);
    return swatch;
  };

  const buildOption = (axisIndex, value, isColorAxis) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = isColorAxis
      ? "gift-swatches__option gift-swatches__option--swatch"
      : "gift-swatches__option gift-swatches__option--pill";
    button.dataset.axis = String(axisIndex);
    button.dataset.value = value;

    if (isColorAxis) {
      button.appendChild(buildSwatch(value));
      const label = document.createElement("span");
      label.className = "visually-hidden";
      label.textContent = value;
      button.appendChild(label);
    } else {
      button.textContent = value;
    }
    return button;
  };

  const enhance = (select) => {
    // Disabled options carry vendor stock labels rather than plain variant
    // titles. Keep their native availability semantics and localized copy.
    if (Array.from(select.options).some((option) => option.disabled)) return;

    const options = readOptions(select);
    if (!options.length) return;

    const axisCount = Math.max(...options.map((option) => option.parts.length));
    if (axisCount < 1) return;

    // A colour axis is only assumed when there are two axes; a lone axis is
    // the size, which every gift product has.
    const colorAxis = axisCount > 1 ? 0 : -1;

    const chosen = (
      options.find((option) => option.value === select.value) || options[0]
    ).parts.slice();

    const root = document.createElement("div");
    root.className = "gift-swatches";

    const rows = [];
    for (let axis = 0; axis < axisCount; axis += 1) {
      const row = document.createElement("div");
      row.className = "gift-swatches__row";
      axisValues(options, axis).forEach((value) => {
        row.appendChild(buildOption(axis, value, axis === colorAxis));
      });
      rows.push(row);
      root.appendChild(row);
    }

    /* Reflect the current combination, and mark combinations that do not
       exist so a sold-out size reads as unavailable rather than silently
       snapping to another colour. */
    const refresh = () => {
      rows.forEach((row, axis) => {
        Array.from(row.children).forEach((button) => {
          const candidate = chosen.slice();
          candidate[axis] = button.dataset.value;
          const variant = findVariant(options, candidate);
          button.classList.toggle(
            "gift-swatches__option--selected",
            chosen[axis] === button.dataset.value,
          );
          button.classList.toggle(
            "gift-swatches__option--unavailable",
            !variant,
          );
          button.disabled = !variant;
        });
      });
    };

    root.addEventListener("click", (event) => {
      const button = event.target.closest(".gift-swatches__option");
      if (!button || button.disabled) return;
      event.preventDefault();

      chosen[Number(button.dataset.axis)] = button.dataset.value;
      const variant = findVariant(options, chosen);
      if (!variant) return;

      setSelectValue(select, variant.value);
      refresh();
    });

    refresh();
    select.setAttribute(ENHANCED, "true");
    select.insertAdjacentElement("afterend", root);
  };

  const selector = `${SELECT}:not([${ENHANCED}])`;

  const scan = (root = document) => {
    if (root !== document && root.matches(selector)) enhance(root);
    root.querySelectorAll(selector).forEach((select) => enhance(select));
  };

  const scanAddedSubtrees = (mutations) => {
    mutations.forEach((mutation) => {
      Array.from(mutation.addedNodes).forEach((node) => {
        if (node.nodeType === 1) scan(node);
      });
    });
  };

  const start = () => {
    scan();
    new MutationObserver(scanAddedSubtrees).observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
